import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import './EauPrompt.css';

/**
 * EAU on the dashboard — the Ethiopian Aviation University entrance package's
 * own prompt.
 *
 * A CARD, always. Every package owns its prompt outright: its own component,
 * its own endpoint, its own switches and copy, and no knowledge that any other
 * package exists. A full-screen modal cannot honour that — two packages each
 * rendering one stack, and the lower dismiss button cannot be reached — so the
 * novelty lives in a "New" badge instead of an interruption.
 *
 * ## Two jobs, and they are not the same job
 *
 *   OFFERING it, to someone who does not hold it. Gated on the launch switch,
 *   on the package being listed, and on the catalog being open to this
 *   student — announcing something they will then be refused is worse than
 *   never telling them.
 *
 *   GETTING BACK TO IT, for someone who already holds it. Gated on nothing.
 *   `show` therefore means "put EAU on the dashboard" and `held` says which of
 *   the two it is.
 *
 * Renders NOTHING unless the server says to.
 */

type Prompt = {
  show: boolean;
  /** They already own it: show the way back, not the pitch. */
  held?: boolean;
  prompt: { title?: string; body?: string; cta?: string; dismiss?: string };
  amount: number | null;
  currency: string;
  /** Where the package lives, from its own manifest. */
  home?: string;
};

const EauPrompt: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Prompt | null>(null);

  useEffect(() => {
    let cancelled = false;
    packagesAPI
      .eauPrompt()
      .then((d) => {
        if (!cancelled && d?.show) setData(d);
      })
      .catch(() => {
        /* a prompt that cannot load is simply not shown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const go = useCallback(() => {
    // Straight to the package, not the storefront. The destination comes from
    // the program's own manifest, with the route as the fallback for an older
    // server.
    navigate(data?.home || '/p/eau-entrance/lessons');
  }, [navigate, data]);

  if (!data) return null;

  const p = data.prompt || {};
  const price = data.amount
    ? `${data.amount} ${data.currency === 'ETB' ? 'birr' : data.currency}`
    : null;

  // Owned. Not an offer — a door.
  if (data.held) {
    return (
      <button className="eaup-card eaup-card--own" onClick={go}>
        <span className="eaup-card__icon" aria-hidden="true">✈️</span>
        <span className="eaup-card__text">
          <strong>EAU Prep</strong>
          <span className="eaup-card__sub">Continue where you left off</span>
        </span>
        <span className="eaup-card__arrow" aria-hidden="true">→</span>
      </button>
    );
  }

  return (
    <button className="eaup-card" onClick={go}>
      <span className="eaup-card__icon" aria-hidden="true">✈️</span>
      <span className="eaup-card__text">
        <strong>
          {p.title || 'EAU Prep'}
          <span className="eaup-card__new"> ✨ New</span>
        </strong>
        <span className="eaup-card__sub">
          Free to start{price ? ` · ${price} for everything` : ''}
        </span>
      </span>
      <span className="eaup-card__arrow" aria-hidden="true">→</span>
    </button>
  );
};

export default EauPrompt;
